/**
 * Destination accounts waiting to be confirmed with their owner, by phone.
 *
 * Registering a payout destination is the one sensitive change on this platform
 * with no human in the loop, and it is the step an account takeover cannot skip:
 * money cannot reach an attacker until the attacker's account is registered.
 * When one is registered from a place this client has never acted from, it is
 * held here until someone has spoken to them.
 *
 * Two things this screen deliberately does not do:
 *
 *   · It never shows a number to dial on a channel that cannot confirm anything.
 *     A number nobody proved, or one changed inside the takeover window, is
 *     exactly the number an attacker would want called — so there is nothing
 *     here to dial by mistake.
 *   · It never shows the account. The agent asks the client to describe what
 *     they did; an agent who reads the details back has done the impostor's
 *     research for them.
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import checksService, {
  type CallBrief,
  type CallChannel,
  type CallOutcome,
  type PendingCheck,
} from '../../services/bankAccountChecks.service';
import authService from '../../services/auth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import {
  CHANNEL_LABEL,
  MIN_NOTES,
  MIN_RELEASE_REASON,
  attemptsLeft,
  byOldestFirst,
  canConfirm,
  describeDistance,
  dialNumber,
  formatCallableFrom,
  isNoteReady,
  isReleaseReady,
  isWaitingForTheNumber,
  queueLabel,
  queueTone,
  whyCannotConfirm,
} from './bankAccountChecks.helpers';

// A number changed in the same week is a WAIT, not a danger: the task becomes
// callable on a known date and needs nobody before then. Red on it read as
// "something is wrong with this client", which is what sent these to managers.
const CHANNEL_TONE: Record<CallChannel, BadgeTone> = {
  ready: 'success',
  no_proven_phone: 'warning',
  phone_recently_changed: 'neutral',
};

export default function BankAccountChecks(): JSX.Element {
  const [rows, setRows] = useState<PendingCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const isManager = ['manager', 'super_admin'].includes(authService.getRole() ?? '');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(byOldestFirst(await checksService.listOpen()));
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load the accounts waiting for a call.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Accounts waiting for a call"
        subtitle="Registered from somewhere this client has never acted from. Not a destination until someone has spoken to them."
      />

      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm" role="alert"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {error}
        </div>
      )}
      {flash && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.success }}>
          {flash}
        </div>
      )}

      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}

      {!loading && rows.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>
          Nothing is waiting. Every destination account was registered from somewhere
          its owner has used before.
        </p>
      )}

      <ul className="space-y-3">
        {rows.map((r) => (
          <CheckCard
            key={r.id}
            row={r}
            isManager={isManager}
            onDone={(msg) => { setFlash(msg); void reload(); }}
            onError={setError}
          />
        ))}
      </ul>
    </div>
  );
}

function CheckCard({ row, isManager, onDone, onError }: Readonly<{
  row: PendingCheck;
  isManager: boolean;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const [open, setOpen] = useState(false);
  const [brief, setBrief] = useState<CallBrief | null>(null);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const escalated = row.verification_status === 'escalated';

  const load = async (): Promise<void> => {
    if (brief !== null) return;
    try {
      setBrief(await checksService.brief(row.id));
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not load the call brief.'));
    }
  };

  const record = async (outcome: CallOutcome): Promise<void> => {
    setBusy(true);
    try {
      await checksService.recordOutcome(row.id, outcome, notes.trim());
      onDone(
        outcome === 'not_the_client'
          ? `${row.first_name}’s account is frozen. Tell them their money is safe and that we will call back to verify who they are.`
          : 'Recorded.',
      );
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not record the call.'));
    } finally {
      setBusy(false);
    }
  };

  const release = async (): Promise<void> => {
    setBusy(true);
    try {
      await checksService.release(row.id, reason.trim());
      onDone('Account released.');
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not release the account.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-2xl p-4"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold" style={{ color: colors.ink }}>
            {row.first_name} {row.last_name}
          </div>
          <p className="text-sm mt-0.5" style={{ color: colors.inkSoft }}>
            {describeDistance(
              row.verification_km_from_usual === null
                ? null
                : Number(row.verification_km_from_usual),
              // The list row has only the distance; the client's own range is
              // computed when the brief is opened, and appears there.
              brief?.usual_range_km,
            )}
          </p>
          <div className="text-xs mt-1" style={{ color: colors.muted }}>
            waiting since {formatDateTime(row.verification_opened_at)} ·
            {' '}{row.verification_call_attempts} call(s) made
            {/* The date belongs on the closed row too. An agent deciding what to
                work on next should not have to open a task to find out that it
                cannot be worked on yet. */}
            {formatCallableFrom(row.callable_from) !== null
              && ` · callable from ${formatCallableFrom(row.callable_from)}`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusBadge tone={queueTone(row)}>
            {queueLabel(row)}
          </StatusBadge>
          <button type="button" onClick={() => { setOpen((v) => !v); void load(); }}
                  className="text-xs px-3 py-1.5 rounded-md font-semibold"
                  style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
            {open ? 'Close' : 'Open'}
          </button>
        </div>
      </div>

      {open && brief && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <StatusBadge tone={CHANNEL_TONE[brief.channel]}>
              {CHANNEL_LABEL[brief.channel]}
            </StatusBadge>
            {dialNumber(brief) !== null && (
              <a href={`tel:${dialNumber(brief)}`} className="text-sm font-semibold underline"
                 style={{ color: colors.navy }}>
                {dialNumber(brief)}
              </a>
            )}
            {formatCallableFrom(brief.callable_from) !== null && (
              <span className="text-xs font-medium" style={{ color: colors.inkSoft }}>
                callable from {formatCallableFrom(brief.callable_from)}
              </span>
            )}
            <span className="text-xs" style={{ color: colors.muted }}>
              {attemptsLeft(brief)} attempt(s) left
            </span>
          </div>

          <p className="text-xs font-semibold mb-1" style={{ color: colors.muted }}>
            WHAT TO SAY
          </p>
          <ul className="text-sm mb-4 space-y-1 list-disc pl-5" style={{ color: colors.inkSoft }}>
            {brief.talking_points.map((point) => <li key={point}>{point}</li>)}
          </ul>

          {whyCannotConfirm(brief) && (
            <div className="mb-3 p-2 rounded-md text-xs"
                 style={isWaitingForTheNumber(brief)
                   ? { background: colors.surfaceMuted, border: `1px solid ${colors.border}`, color: colors.inkSoft }
                   : { background: colors.warningBg, border: '1px solid #FCD34D', color: colors.ink }}>
              {whyCannotConfirm(brief)}
            </div>
          )}

          {!escalated && (
            <>
              <label htmlFor={`notes-${row.id}`} className="block text-xs font-semibold mb-1"
                     style={{ color: colors.inkSoft }}>
                What happened on the call?
              </label>
              <textarea
                id={`notes-${row.id}`}
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 2000))}
                placeholder="Client described adding an account to receive her withdrawal."
                disabled={busy}
                className="w-full px-3 py-2 rounded-lg text-sm mb-1"
                style={{ border: `1px solid ${colors.border}` }}
              />
              <p className="text-xs mb-3" style={{ color: colors.muted }}>
                At least {MIN_NOTES} characters.
              </p>
              <div className="flex gap-2 flex-wrap">
                <button type="button"
                        disabled={!canConfirm(brief) || !isNoteReady(notes) || busy}
                        onClick={() => { void record('confirmed'); }}
                        className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                        style={{ background: colors.navy, color: '#FFFFFF' }}>
                  It was them
                </button>
                {canConfirm(brief) && (
                  <button type="button" disabled={!isNoteReady(notes) || busy}
                          onClick={() => { void record('no_answer'); }}
                          className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                          style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
                    No answer
                  </button>
                )}
                <button type="button" disabled={!isNoteReady(notes) || busy}
                        onClick={() => { void record('not_the_client'); }}
                        className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                        style={{ background: colors.danger, color: '#FFFFFF' }}>
                  It was NOT them
                </button>
              </div>
            </>
          )}

          {escalated && isManager && (
            <>
              <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
                The calls did not reach this client. Releasing it is your judgement, made
                without them confirming on a channel we trust — so say how you satisfied
                yourself, and it is recorded under your name.
              </p>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 2000))}
                placeholder="Verified in branch against her passport on 24 September."
                disabled={busy}
                className="w-full px-3 py-2 rounded-lg text-sm mb-3"
                style={{ border: `1px solid ${colors.border}` }}
                aria-label="How you satisfied yourself this is the client"
              />
              <button type="button" disabled={!isReleaseReady(reason) || busy}
                      onClick={() => { void release(); }}
                      className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                      style={{ background: colors.navy, color: '#FFFFFF' }}>
                Release this account
              </button>
              <p className="text-xs mt-2" style={{ color: colors.muted }}>
                At least {MIN_RELEASE_REASON} characters.
              </p>
            </>
          )}

          {escalated && !isManager && (
            <p className="text-sm" style={{ color: colors.muted }}>
              A manager decides this one.
            </p>
          )}
        </div>
      )}
    </li>
  );
}
